import { useState, useEffect } from 'react';
import { X, Bot, Webhook, Linkedin, AtSign, Mail, Check, Loader2, Rocket, Clock, AlertCircle, ExternalLink, Share2, Download } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { key: 'linkedin_post', label: 'LinkedIn', Icon: Linkedin, color: '#0A66C2' },
  { key: 'x_thread', label: 'X', Icon: AtSign, color: '#1DA1F2' },
  { key: 'email_draft', label: 'Email', Icon: Mail, color: '#FF7A18' },
];

const EVENTS = [
  { key: 'strategy_generated', label: 'Strategy Generated', desc: 'When a new analysis completes' },
  { key: 'content_ready', label: 'Content Ready', desc: 'When content is generated' },
  { key: 'daily_edge_alert', label: 'Daily Edge Alert', desc: 'Daily strategy refresh notification' },
];

export function GrowthAgentModal({ isOpen, onClose, projectId, platform, content, opportunity, variation, score, goal }) {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [events, setEvents] = useState(['strategy_generated', 'content_ready', 'daily_edge_alert']);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [generatingCard, setGeneratingCard] = useState(false);
  const [cardImage, setCardImage] = useState(null);

  // Load saved config + history + share data
  useEffect(() => {
    if (!isOpen || !projectId) return;
    const load = async () => {
      setLoadingHistory(true);
      try {
        const [configRes, historyRes, shareRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/deploy/config`),
          axios.get(`${API}/projects/${projectId}/deployments`),
          axios.get(`${API}/projects/${projectId}/share-data`).catch(() => ({ data: null }))
        ]);
        if (configRes.data?.webhook_url) {
          setWebhookUrl(configRes.data.webhook_url);
          if (configRes.data.events) setEvents(configRes.data.events);
        }
        setHistory(historyRes.data || []);
        if (shareRes.data) setShareData(shareRes.data);
      } catch {} finally { setLoadingHistory(false); }
    };
    load();
  }, [isOpen, projectId]);

  const toggleEvent = (key) => {
    setEvents(prev => prev.includes(key) ? prev.filter(e => e !== key) : [...prev, key]);
  };

  const handleDeploy = async () => {
    if (!webhookUrl.trim()) return;
    setDeploying(true);
    setDeployResult(null);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/deploy`, {
        webhook_url: webhookUrl.trim(),
        platform: platform || 'linkedin_post',
        events,
        content: content || '',
        opportunity: opportunity || {},
        variation: variation || 'safe',
        goal: goal || '',
        score: score || 0,
      });
      setDeployResult(res.data);
      // Refresh history
      const h = await axios.get(`${API}/projects/${projectId}/deployments`);
      setHistory(h.data || []);
    } catch (err) {
      setDeployResult({ status: 'failed', error: err.message });
    } finally { setDeploying(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const platformLabel = PLATFORMS.find(p => p.key === platform)?.label || 'LinkedIn';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="growth-agent-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 rounded-2xl overflow-hidden" style={{ background: '#0E0E14', border: '1px solid #1F1F28', boxShadow: '0 0 60px rgba(124,92,255,0.08), 0 0 120px rgba(255,122,24,0.04)' }}>
        {/* Glow line */}
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #7C5CFF, #FF7A18, #7C5CFF)', opacity: 0.7 }} />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#1F1F28]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(124,92,255,0.2), rgba(255,122,24,0.15))' }}>
              <Bot className="w-4 h-4 text-[#FF7A18]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Activate Growth Agent</h2>
              <p className="text-[10px] text-zinc-500 font-mono">Analysis → Execution</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-modal-btn" className="text-zinc-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-[#1F1F28]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Success State + Feedback + Share */}
        {deployResult?.status === 'success' ? (
          <div className="px-5 py-5 space-y-3 max-h-[70vh] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#1F1F28 transparent' }} data-testid="deploy-success">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-2" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(124,92,255,0.1))', border: '1px solid rgba(16,185,129,0.2)' }}>
                <Rocket className="w-6 h-6 text-[#10B981]" />
              </div>
              <h3 className="text-base font-bold text-white mb-0.5">Strategy Live</h3>
              <div className="space-y-1 text-[12px]">
                <p className="text-zinc-300 flex items-center justify-center gap-1"><Check className="w-3 h-3 text-[#10B981]" />Posted to {platformLabel}</p>
                <p className="text-zinc-500 flex items-center justify-center gap-1"><Clock className="w-3 h-3 text-[#7C5CFF]" />Tracking engagement</p>
              </div>
            </div>

            {/* Performance Feedback */}
            {!feedbackSent ? (
              <div className="pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }} data-testid="feedback-section">
                <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2 text-center">How did this perform?</p>
                <div className="flex items-center justify-center gap-2">
                  {[
                    { rating: 'good', label: 'Good', color: '#22C55E', emoji: '↑' },
                    { rating: 'average', label: 'Average', color: '#F59E0B', emoji: '→' },
                    { rating: 'poor', label: 'Poor', color: '#EF4444', emoji: '↓' },
                  ].map(({ rating, label, color, emoji }) => (
                    <button key={rating} data-testid={`feedback-${rating}`}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/deployments/${deployResult.id}/feedback`, { rating });
                          setFeedbackSent(true);
                        } catch {}
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all hover:-translate-y-0.5"
                      style={{ background: `${color}10`, border: `1px solid ${color}20`, color }}>
                      <span className="text-base">{emoji}</span>{label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }} data-testid="feedback-confirmed">
                <p className="text-[11px] text-[#22C55E] flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" />Feedback recorded — learning engine updated</p>
              </div>
            )}

            {/* Share Your Move */}
            <div className="pt-3 border-t space-y-2" style={{ borderColor: 'rgba(255,255,255,0.06)' }} data-testid="share-section">
              <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 text-center">Share your move</p>

              {/* Compare Edge */}
              {shareData && shareData.percentile > 0 && (
                <div className="rounded-xl px-3 py-2 text-center" style={{ background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.12)' }} data-testid="compare-edge">
                  <p className="text-[11px] text-zinc-300">Your strategy scored <span className="text-[#7C5CFF] font-bold">{shareData.score}</span></p>
                  <p className="text-[10px] text-[#7C5CFF] font-medium">You're ahead of {shareData.percentile}% of products</p>
                </div>
              )}

              {/* Share buttons */}
              <div className="flex items-center gap-2">
                <button data-testid="share-x" onClick={() => {
                  const text = shareData?.share_texts?.x || `Just deployed a ${score}/100 strategy with NextMove`;
                  window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium text-zinc-300 hover:text-white transition-all hover:-translate-y-0.5"
                  style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <AtSign className="w-3.5 h-3.5" />Post on X
                </button>
                <button data-testid="share-linkedin" onClick={() => {
                  const text = shareData?.share_texts?.linkedin || '';
                  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://nextmove.ai')}&summary=${encodeURIComponent(text)}`, '_blank');
                }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium text-zinc-300 hover:text-white transition-all hover:-translate-y-0.5"
                  style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Linkedin className="w-3.5 h-3.5" />Share on LinkedIn
                </button>
              </div>

              {/* Generate Strategy Card */}
              {!cardImage ? (
                <button data-testid="generate-card-btn" onClick={async () => {
                  setGeneratingCard(true);
                  try {
                    const res = await axios.post(`${API}/projects/${projectId}/generate-card`, {
                      score, mode: shareData?.mode, product: shareData?.product
                    }, { timeout: 120000 });
                    if (res.data?.image_base64) setCardImage(res.data.image_base64);
                  } catch {} finally { setGeneratingCard(false); }
                }} disabled={generatingCard}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, rgba(124,92,255,0.1), rgba(255,138,61,0.08))', border: '1px solid rgba(124,92,255,0.15)', color: '#7C5CFF' }}>
                  {generatingCard ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Generating AI card...</> : <><Share2 className="w-3.5 h-3.5" />Generate Strategy Card</>}
                </button>
              ) : (
                <div className="space-y-2" data-testid="card-preview">
                  <img src={`data:image/png;base64,${cardImage}`} alt="Strategy Card" className="w-full rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.06)' }} />
                  <button data-testid="download-card" onClick={() => {
                    const link = document.createElement('a');
                    link.href = `data:image/png;base64,${cardImage}`;
                    link.download = `nextmove-strategy-${projectId.slice(0,8)}.png`;
                    link.click();
                  }}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium text-zinc-300 hover:text-white transition-all"
                    style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <Download className="w-3.5 h-3.5" />Download Card
                  </button>
                </div>
              )}
            </div>

            <button onClick={() => { setDeployResult(null); setFeedbackSent(false); setCardImage(null); }} data-testid="deploy-another-btn"
              className="text-xs text-[#7C5CFF] hover:text-white transition-colors font-medium w-full text-center pt-1">
              Deploy another
            </button>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#1F1F28 transparent' }}>

            {/* Platform Indicator */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Deploying to</p>
              <div className="flex gap-2">
                {PLATFORMS.map(({ key, label, Icon, color }) => (
                  <div key={key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium ${platform === key ? 'text-white' : 'text-zinc-600'}`}
                    style={platform === key ? { background: `${color}15`, border: `1px solid ${color}30` } : { background: '#141418', border: '1px solid #1F1F28' }}>
                    <Icon className="w-3 h-3" style={{ color: platform === key ? color : undefined }} />{label}
                    {platform === key && <Check className="w-3 h-3" style={{ color }} />}
                  </div>
                ))}
              </div>
            </div>

            {/* Webhook URL */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1">
                <Webhook className="w-3 h-3" />Webhook / Custom Agent
              </p>
              <input type="url" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
                placeholder="https://your-agent.com/webhook"
                data-testid="webhook-url-input"
                className="w-full bg-[#0B0B0F] text-zinc-100 text-sm h-9 px-3 rounded-xl outline-none transition-all placeholder:text-zinc-600 focus:ring-1 focus:ring-[#7C5CFF]/40"
                style={{ border: '1px solid #1F1F28' }} />
              <p className="text-[9px] text-zinc-600 mt-1 font-mono">Works with Zapier, Make, n8n, or any webhook endpoint</p>
            </div>

            {/* Event Subscriptions */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Events</p>
              <div className="space-y-1.5">
                {EVENTS.map(({ key, label, desc }) => (
                  <label key={key} className="flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer transition-all hover:bg-[#141418]"
                    style={{ background: events.includes(key) ? '#141418' : 'transparent', border: `1px solid ${events.includes(key) ? '#1F1F28' : 'transparent'}` }}
                    data-testid={`event-${key}`}>
                    <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all ${events.includes(key) ? 'bg-[#7C5CFF]' : 'bg-[#1F1F28]'}`}
                      onClick={(e) => { e.preventDefault(); toggleEvent(key); }}>
                      {events.includes(key) && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[12px] text-zinc-200 font-medium">{label}</span>
                      <p className="text-[10px] text-zinc-600">{desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Payload Preview */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Payload preview</p>
              <div className="rounded-xl px-3 py-2 text-[10px] font-mono text-zinc-500 leading-relaxed overflow-x-auto" style={{ background: '#0B0B0F', border: '1px solid #1A1A22' }}>
                <span className="text-zinc-600">{'{'}</span>
                <br />&nbsp;&nbsp;<span className="text-[#7C5CFF]">"platform"</span>: <span className="text-[#10B981]">"{platformLabel}"</span>,
                <br />&nbsp;&nbsp;<span className="text-[#7C5CFF]">"variation"</span>: <span className="text-[#10B981]">"{variation}"</span>,
                <br />&nbsp;&nbsp;<span className="text-[#7C5CFF]">"goal"</span>: <span className="text-[#10B981]">"{goal}"</span>,
                <br />&nbsp;&nbsp;<span className="text-[#7C5CFF]">"score"</span>: <span className="text-[#F59E0B]">{score}</span>,
                <br />&nbsp;&nbsp;<span className="text-[#7C5CFF]">"content"</span>: <span className="text-zinc-600">{'{ hook, body, cta }'}</span>
                <br /><span className="text-zinc-600">{'}'}</span>
              </div>
            </div>

            {/* Deploy Error */}
            {deployResult?.status === 'failed' && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-[11px]" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="deploy-error">
                <AlertCircle className="w-3.5 h-3.5 text-[#EF4444] flex-shrink-0" />
                <span className="text-[#EF4444]">Deployment failed. Check your webhook URL and try again.</span>
              </div>
            )}

            {/* Deploy Button */}
            <button onClick={handleDeploy} disabled={deploying || !webhookUrl.trim()} data-testid="deploy-strategy-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
              style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF7A18)', boxShadow: '0 4px 20px rgba(124,92,255,0.25), 0 4px 20px rgba(255,122,24,0.15)' }}>
              {deploying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              {deploying ? 'Deploying...' : 'Deploy Strategy'}
            </button>

            {/* Deployment History */}
            {history.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Recent Deployments</p>
                <div className="space-y-1.5">
                  {history.slice(0, 3).map((dep) => {
                    const p = PLATFORMS.find(pl => pl.key === dep.platform);
                    const PIcon = p?.Icon || Webhook;
                    return (
                      <div key={dep.id} className="flex items-center gap-2.5 px-3 py-2 rounded-xl" style={{ background: '#141418', border: '1px solid #1A1A22' }} data-testid={`deployment-${dep.id}`}>
                        <PIcon className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-[11px] text-zinc-300 font-medium">{p?.label || dep.platform}</span>
                        <div className="flex-1" />
                        <span className={`text-[10px] font-mono ${dep.status === 'success' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                          {dep.status === 'success' ? 'Live' : 'Failed'}
                        </span>
                        <span className="text-[10px] text-zinc-600 font-mono">{formatTime(dep.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer hint */}
        <div className="px-5 py-2.5 border-t border-[#1F1F28] flex items-center justify-between">
          <p className="text-[9px] text-zinc-600 font-mono">Structured JSON payload • Works with Zapier, Make, n8n</p>
          <span className="text-[9px] font-mono px-2 py-0.5 rounded-md" style={{ color: '#7C5CFF', background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.12)' }}>Phase 1: Webhook</span>
        </div>
      </div>
    </div>
  );
}
